import { z } from 'zod';
import { idSchema } from './common';
import { languageSchema } from './enums';
import { rowSchema } from './rows';

/** SPEC §7 — approve / bulk approve / un-approve. */

/** Why a row could not be approved. */
export const APPROVAL_SKIP_REASONS = [
  'not_generated',
  'stale',
  'blank_text',
  'version_conflict',
  /**
   * A concurrent select-take moved `currentTakeId` while the row was being published.
   * SPEC-GAP: §7 does not name this outcome; it is a 409 rather than a silent
   * half-apply, so the reviewer's latest choice always wins and can be approved again.
   */
  'take_changed',
  /** A single-asset regeneration is in flight; approval waits for the A/B choice. */
  'regenerating',
  'already_approved',
  'row_removed',
  'transcode_failed',
  'upload_failed',
] as const;
export const approvalSkipReasonSchema = z.enum(APPROVAL_SKIP_REASONS);
export type ApprovalSkipReason = z.infer<typeof approvalSkipReasonSchema>;

export const publishedObjectSchema = z.object({
  language: languageSchema,
  publishedPath: z.string(),
  deliveredPath: z.string().nullable(),
  generation: z.string(),
  previousGeneration: z.string().nullable(),
  archivedPath: z.string().nullable(),
  publicUrl: z.string(),
});
export type PublishedObject = z.infer<typeof publishedObjectSchema>;

/** POST /api/rows/:id/approve */
export const approveRowRequestSchema = z.object({
  version: z.number().int().min(1),
});
export type ApproveRowRequest = z.infer<typeof approveRowRequestSchema>;

export const approveRowResponseSchema = z.object({
  row: rowSchema,
  publishedSlug: z.string(),
  published: z.array(publishedObjectSchema),
});
export type ApproveRowResponse = z.infer<typeof approveRowResponseSchema>;

/** 422 body when the row is not eligible. */
export const approvalNotEligibleSchema = z.object({
  code: z.literal('approval_not_eligible'),
  message: z.string(),
  reasons: z.array(
    z.object({ language: languageSchema.nullable(), reason: approvalSkipReasonSchema }),
  ),
});
export type ApprovalNotEligible = z.infer<typeof approvalNotEligibleSchema>;

/** POST /api/batches/:id/approve-bulk — skips ineligible rows, reports per row. */
export const bulkApproveRequestSchema = z.object({
  rowIds: z.array(idSchema).min(1).max(1000),
});
export type BulkApproveRequest = z.infer<typeof bulkApproveRequestSchema>;

export const bulkApproveOutcomeSchema = z.object({
  rowId: idSchema,
  rowIndex: z.number().int().min(0),
  approved: z.boolean(),
  publishedSlug: z.string().nullable(),
  reasons: z.array(approvalSkipReasonSchema),
  message: z.string().nullable(),
});
export type BulkApproveOutcome = z.infer<typeof bulkApproveOutcomeSchema>;

export const bulkApproveResponseSchema = z.object({
  approved: z.number().int().min(0),
  skipped: z.number().int().min(0),
  outcomes: z.array(bulkApproveOutcomeSchema),
});
export type BulkApproveResponse = z.infer<typeof bulkApproveResponseSchema>;

/** POST /api/rows/:id/unapprove — SUPER_ADMIN. Archives then deletes published objects. */
export const unapproveRowResponseSchema = z.object({
  row: rowSchema,
  archivedPaths: z.array(z.string()),
  deletedPaths: z.array(z.string()),
});
export type UnapproveRowResponse = z.infer<typeof unapproveRowResponseSchema>;
